import type { ReactNode } from 'react'
import { ArrowUpDownIcon } from 'lucide-react'
import { Button, cn, TableCell, TableHead } from '@rentflowdev/ui'
import { CURRENCY_SIGNS } from '@/types'
import { formatPhone } from '@/lib/phone'

/**
 * The pieces the active and completed deal lists share.
 *
 * The two tables were written separately and had drifted into different
 * answers to the same questions: one showed a thumbnail and the other did not,
 * one tucked the price under the address while the other gave it a column, the
 * sort header was implemented twice with different padding, and the columns
 * were 140px on one page and 184px on the next. Moving from one list to the
 * other read as moving between two products.
 *
 * The column widths live here so the two cannot drift again — a change lands
 * on both or on neither.
 */

/** Shared column widths. The object column takes whatever is left. */
export const DEALS_COL = {
	client: 'w-[184px]',
	price: 'w-[140px]',
	// «Источник обращения» — колонка шире прочих, иначе заголовок не помещается.
	source: 'w-[180px]',
	status: 'w-[140px]',
	date: 'w-[110px]',
	action: 'w-16',
} as const

interface SortHeadProps<F extends string> {
	field: F
	active: boolean
	dir: 'asc' | 'desc'
	onToggle: (field: F) => void
	className?: string
	children: ReactNode
}

export function DealSortHead<F extends string>({
	field,
	active,
	onToggle,
	className,
	children,
}: SortHeadProps<F>) {
	return (
		<TableHead className={cn('px-0 py-1 text-(--color-muted-foreground)', className)}>
			<Button
				variant="ghost"
				iconRight={<ArrowUpDownIcon className={cn('size-3.5', active && 'text-(--color-foreground)')} />}
				className="h-auto gap-1 px-3 py-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
				onClick={() => onToggle(field)}
			>
				{children}
			</Button>
		</TableHead>
	)
}

interface DealPropertyCellProps {
	image?: string
	title: string
	address?: string
	/** Given when the deal has a property — the object opens from the address. */
	onOpenProperty?: () => void
}

/**
 * The object cell: a thumbnail, the object, its address beneath.
 *
 * The address itself opens the object. It used to take a separate house icon
 * in the last column, which meant aiming at a 16px target for the commonest
 * jump on the page — and in the completed list it sat next to a second icon
 * that did something else entirely. The row still opens the deal.
 *
 * Truncated deliberately: the active list printed the address untruncated, so
 * a long one ran over the next column and the two strings overlapped.
 */
export function DealPropertyCell({ image, title, address, onOpenProperty }: DealPropertyCellProps) {
	const heading = onOpenProperty ? (
		<button
			type="button"
			// The row navigates to the deal; this cell navigates to the object.
			onClick={(e) => { e.stopPropagation(); onOpenProperty() }}
			className="truncate text-left text-sm font-[500] leading-none text-(--color-foreground) hover:underline"
		>
			{title}
		</button>
	) : (
		<span className="truncate text-sm font-[500] leading-none text-(--color-foreground)">
			{title}
		</span>
	)

	return (
		<TableCell className="px-4">
			<div className="flex items-center gap-3">
				{image ? (
					<img src={image} alt={title} className="size-12 shrink-0 rounded-xl object-cover" />
				) : (
					<div className="size-12 shrink-0 rounded-xl bg-(--color-muted)" />
				)}
				<div className="flex min-w-0 flex-col gap-1">
					{heading}
					{address && (
						<span className="truncate text-sm leading-5 text-(--color-muted-foreground)">
							{address}
						</span>
					)}
				</div>
			</div>
		</TableCell>
	)
}

interface DealClientCellProps {
	name?: string
	phone?: string
	email?: string
	fallback?: string
}

/**
 * The client: who they are, and how to reach them beneath.
 *
 * One column rather than three. A manager scanning the list wants the name and
 * a way to call — splitting those into separate columns would push the object
 * off the screen to show two fields that are read together anyway.
 */
export function DealClientCell({ name, phone, email, fallback }: DealClientCellProps) {
	const contact = formatPhone(phone) || email
	return (
		<TableCell className={`${DEALS_COL.client} px-4`}>
			<div className="min-w-0">
				<p className="truncate text-sm text-(--color-foreground)">{name ?? fallback ?? '—'}</p>
				{contact && (
					<p className="truncate text-xs text-(--color-muted-foreground)">{contact}</p>
				)}
			</div>
		</TableCell>
	)
}

/**
 * The monthly rate, as the server worked it out.
 *
 * `propertyMonthlyPrice` is already converted from whatever the terms are
 * quoted in — per year, per m² per year, per seat. Falls back to the stored
 * amount when the server could not convert (a per-unit rate with no area), so
 * the cell shows the figure that exists rather than going blank.
 */
export function formatMonthlyPrice(deal: {
	propertyMonthlyPrice?: string
	propertyPrice?: string
	propertyCurrency?: string
}): string | undefined {
	const raw = deal.propertyMonthlyPrice ?? deal.propertyPrice
	const grouped = formatDealPrice(raw)
	if (!grouped) return undefined
	const symbol = deal.propertyCurrency ? CURRENCY_SIGNS[deal.propertyCurrency] : undefined
	return symbol ? `${grouped} ${symbol}` : grouped
}

/**
 * То же, но с периодом: «40 000 ₽/мес».
 *
 * На доске у цены нет заголовка колонки, который сказал бы, за что она — без
 * периода сумма читается как стоимость объекта, а не как ставка.
 */
export function formatMonthlyPriceWithPeriod(deal: {
	propertyMonthlyPrice?: string
	propertyPrice?: string
	propertyCurrency?: string
}): string | undefined {
	const amount = formatMonthlyPrice(deal)
	return amount ? `${amount}/мес` : undefined
}

/** Short date, as both lists print it. */
export function formatDealDate(iso: string): string {
	return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
}

/**
 * The deal's price, grouped so it can be read at a glance.
 *
 * `Deal.propertyPrice` is a bare string of digits with no currency of its own,
 * so it was printed as «109266» and the reader had to count places. Grouping is
 * safe; adding a symbol is not — the currency lives on the rent terms, and
 * stamping the account's default onto a stored figure would restate an amount
 * that was agreed in something else.
 *
 * A value that already carries formatting (a symbol, a period) is passed
 * through untouched: the property fallback composes one, and reformatting it
 * would strip what it added.
 */
export function formatDealPrice(value?: string | null): string | undefined {
	if (!value) return undefined
	if (!/^\d+$/.test(value.trim())) return value
	return Number(value).toLocaleString('ru-RU')
}
